import tokens from '@kluein/design-tokens';
import { Box, Text } from '@kluein/klue-ui';
import { Menu, Button } from '@mantine/core';
import { Eye, CaretDown, Info } from '@phosphor-icons/react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import store from 'store';
import TEST_IDS from 'test-ids';
import TRACKING_IDS from 'tracking-ids';

import styles from './AskKlueVisibilityGroups.module.css';

import type { RootState } from 'store/store.types';

type AskKlueVisibilityGroupsProps = {
  visibilityGroupId?: number;
};

export const getVisibilityGroupURL = (groupId: number | undefined) => {
  const url = new URL(window.location.href);
  url.searchParams.set('askKlueVisibilityGroupId', groupId?.toString() ?? '');

  return url.href;
};

function AskKlueVisibilityGroups({
  visibilityGroupId,
}: AskKlueVisibilityGroupsProps) {
  const { t } = useTranslation(['Dashboard', 'Common']);
  const [opened, setOpened] = useState(false);

  const viewAsOptions = useSelector((state: RootState) =>
    store.select.visibilityGroups.viewAs(state).map((group) => {
      const href = getVisibilityGroupURL(group.id);

      return {
        key: group.id.toString(),
        label:
          'name' in group
            ? group.name
            : t('Common:visibilityTypeTitles.fullAccessUsers'),
        href,
      };
    }),
  );
  const currentVisibilityGroup = viewAsOptions.find(
    (option) => option.key === visibilityGroupId?.toString(),
  );

  const textLabel =
    currentVisibilityGroup !== undefined ? 'previewingMessage' : 'message';

  return (
    <Box
      direction="row"
      border={{ color: tokens.color.primitives.blue[450], size: '1px' }}
      background={tokens.color.primitives.blue[50]}
      round="5px"
      pad="medium"
      gap="xsmall"
      justify="center"
      align="center"
      margin={{ bottom: 'small' }}
    >
      <Info
        size={tokens.iconSize.medium}
        color={tokens.color.primitives.blue[450]}
      />
      <Text fontWeight="semibold">
        <Trans
          i18nKey={`Dashboard:askKlue.answerModal.viewAs.${textLabel}`}
          components={{
            bold: <strong />,
          }}
          values={{
            visibilityGroup: currentVisibilityGroup?.label,
          }}
        />
      </Text>

      <Menu
        shadow="md"
        opened={opened}
        onChange={setOpened}
        withinPortal={true}
      >
        <Menu.Target>
          <Button
            variant="transparent"
            size="xs"
            leftSection={<Eye size={12} />}
            rightSection={<CaretDown size={12} />}
            data-test-id={TEST_IDS.askKlue.viewAs.select}
            data-tracking-id={TRACKING_IDS.askKlue.viewAs.select}
            className={styles.viewAsButton}
          >
            {currentVisibilityGroup !== undefined
              ? currentVisibilityGroup?.label
              : t('Dashboard:askKlue.answerModal.viewAs.select')}
          </Button>
        </Menu.Target>

        <Menu.Dropdown className={styles.menuDropdown}>
          {viewAsOptions.map((option) => (
            <Menu.Item
              key={option.key}
              component="a"
              href={option.href}
              target="_blank"
              className={styles.menuItem}
            >
              {option.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
}

export default AskKlueVisibilityGroups;
